import { chapterNumber } from "../lib/timeline/book/ChapterId";
import type { ChapterSource } from "./schema/chapter-source";
import type { KeyItem } from "./schema/key-items";

/** A book's item, for its entries: who had it and in which chapters. */
export interface KeyItemEntry {
  id: string;
  name: string;
  description?: string;
  chapterIds: string[];
  fate?: { label: string; chapterId: string };
  holders: { characterId: string; displayName: string; chapterIds: string[] }[];
}

const chapterOf = (sceneId: string): string => sceneId.split("-")[0] ?? sceneId;
const inOrder = (ids: Iterable<string>): string[] => [...new Set(ids)]
  .sort((a, b) => (chapterNumber(a) ?? 0) - (chapterNumber(b) ?? 0));

function wornHere(item: KeyItem, sceneId: string): { invisibleCharacterIds?: string[] } {
  const wearers = item.worn?.scenes[sceneId];
  return wearers === undefined ? {} : { invisibleCharacterIds: wearers };
}

/** Puts each item on the scenes it appears in. */
export function attachKeyItems(source: ChapterSource, items: readonly KeyItem[]): ChapterSource {
  return {
    ...source,
    scenes: {
      ...source.scenes,
      scenes: source.scenes.scenes.map((scene) => {
        const here = items.flatMap((item): NonNullable<typeof scene.keyItems> => {
          const secretIds = item.secretScenes?.[scene.id];
          if (secretIds !== undefined) {
            return [{ itemId: item.id, name: item.name, characterIds: secretIds, secret: true }];
          }
          const characterIds = item.scenes[scene.id];
          const fate = item.fate?.sceneId === scene.id ? item.fate : undefined;
          if (fate !== undefined) {
            return [{
              itemId: item.id,
              name: item.name,
              characterIds: characterIds ?? fate.characterIds,
              fate: fate.label,
            }];
          }
          return characterIds === undefined
            ? []
            : [{
              itemId: item.id,
              name: item.name,
              characterIds,
              ...wornHere(item, scene.id),
              ...(item.glowScenes?.includes(scene.id) === true ? { glow: true } : {}),
            }];
        });
        return here.length === 0 ? scene : { ...scene, keyItems: here };
      }),
    },
  };
}

/** Item lists can outlive a redraft, so a scene that is gone should say so. */
export function missingKeyItemScenes(
  items: readonly KeyItem[],
  sceneIds: ReadonlySet<string>,
): string[] {
  return items.flatMap((item) => [
    ...Object.keys(item.scenes),
    ...Object.keys(item.secretScenes ?? {}),
    ...Object.keys(item.worn?.scenes ?? {}),
    ...(item.glowScenes ?? []),
    ...(item.fate === undefined ? [] : [item.fate.sceneId]),
  ]
    .filter((sceneId) => !sceneIds.has(sceneId))
    .map((sceneId) => `${item.name}: ${sceneId}`));
}

export function buildKeyItemEntries(
  items: readonly KeyItem[],
  nameOf: (characterId: string) => string,
): KeyItemEntry[] {
  return items.map((item) => {
    const scenes = Object.entries(item.scenes);
    const holderIds = new Set(scenes.flatMap(([, ids]) => ids));
    return {
      id: item.id,
      name: item.name,
      ...(item.description === undefined ? {} : { description: item.description }),
      chapterIds: inOrder(scenes.map(([sceneId]) => chapterOf(sceneId))),
      ...(item.fate === undefined
        ? {}
        : { fate: { label: item.fate.label, chapterId: chapterOf(item.fate.sceneId) } }),
      // Sitting in a ship is not being held: only people with a name in the cast count.
      holders: [...holderIds].filter((id) => nameOf(id) !== id).map((characterId) => ({
        characterId,
        displayName: nameOf(characterId),
        chapterIds: inOrder(scenes
          .filter(([, ids]) => ids.includes(characterId))
          .map(([sceneId]) => chapterOf(sceneId))),
      })),
    };
  });
}

/** What to show in a scene. Secret items wait for spoilers, and the list stays stable if none. */
export function visibleKeyItems<T extends { secret?: boolean }>(
  items: readonly T[],
  spoilersOn: boolean,
): readonly T[] {
  return spoilersOn || !items.some((item) => item.secret === true)
    ? items
    : items.filter((item) => item.secret !== true);
}

/** Everyone an item is hiding right now, and which item is hiding them. */
export function invisibleCharacters(
  items: readonly { itemId: string; invisibleCharacterIds?: readonly string[] }[],
): ReadonlyMap<string, string> {
  return new Map(items.flatMap((item) => (item.invisibleCharacterIds ?? [])
    .map((characterId): [string, string] => [characterId, item.itemId])));
}

/** "Chapter 10" or "Chapters 3, 4". */
export function describeChapterList(chapterIds: readonly string[]): string {
  const numbers = chapterIds.map((id) => chapterNumber(id) ?? id).join(", ");
  return `${chapterIds.length === 1 ? "Chapter" : "Chapters"} ${numbers}`;
}
